import { Controller } from "@hotwired/stimulus";
import Sortable, { AutoScroll } from "sortablejs/modular/sortable.core.esm.js";

Sortable.mount(new AutoScroll());

/**
 * Sortable Controller
 *
 * This controller manages the sorting functionality for elements within a
 * container.
 *
 * Requirements:
 * 1. A container element with data-sortable-target="container"
 * 2. Elements with data-sortable-handle attribute to be used as drag handles
 * 3. Hidden input fields for each sortable item to store the item IDs
 * 4. A form (can be standalone) element with data-sortable-target="form"
 *   - If the form is standalone, the sortItemIdsField inputs must be linked
 *     via form: "form_id".
 *
 * Optional:
 * 1. A data-sortable-animation-value attribute to set the animation speed.
 *    Default is 150ms.
 * 2. A data-sortable-group attribute to identify different sortable groups
 *    (e.g., "items" for menu items, "content" for sections and features)
 * 3. A data-sortable-type attribute to identify the type of sortable element
 *    (e.g., "section" or "feature")
 *
 * The controller uses SortableJS to enable drag-and-drop functionality.
 * It submits the form with the new order when sorting is complete.
 *
 * Special handling for menu sections:
 * - When sorting menu sections, menu item sorting is temporarily disabled
 * - Menu sections show a compact preview while being dragged
 * - The number of visible items within a dragged section is limited
 *
 * Example usage with nested form:
 * <div data-controller="sortable" data-sortable-animation-value="150">
 *   <form data-sortable-target="form">
 *     <div data-sortable-target="container">
 *       <div data-sortable-type="section">
 *         <span data-sortable-handle>☰</span>
 *         Section 1
 *         <input type="hidden" name="item_ids[]" value="section_1">
 *       </div>
 *     </div>
 *   </form>
 * </div>
 *
 * Example usage with standalone form:
 * <div data-controller="sortable" data-sortable-animation-value="150">
 *   <form id="menu-item-sort-form" data-sortable-target="form">
 *     <!-- Form is intentionally left empty -->
 *   </form>
 *   <div data-sortable-target="container">
 *     <div>
 *       <span data-sortable-handle>☰</span>
 *       Item 1
 *       <input type="hidden" name="item_ids[]" value="1" form="menu-item-sort-form">
 *     </div>
 *     <div>
 *       <span data-sortable-handle>☰</span>
 *       Item 2
 *       <input type="hidden" name="item_ids[]" value="2" form="menu-item-sort-form">
 *     </div>
 *   </div>
 * </div>
 */
export default class extends Controller {
  static targets = ["container", "form"];
  static values = {
    animation: {
      type: Number,
      default: 150,
    },
    autoSubmit: {
      type: Boolean,
      default: true,
    },
    rootElementSelector: {
      type: String,
    },
  };

  connect() {
    if (window.isSorting) {
      return;
    }

    // Default options
    const options = {
      scroll: true,
      forceAutoScrollFallback: true,
      scrollSensitivity: 100,
      scrollSpeed: 25,
      bubbleScroll: true,

      animation: this.animationValue,
      handle: "[data-sortable-handle]",
      forceFallback: true,
      fallbackOnBody: true,
      fallbackClass: "sortable-fallback",
      onStart: this.onStart.bind(this),
      onEnd: this.onEnd.bind(this),
    };

    this.sortable = Sortable.create(this.containerTarget, options);

    if (
      window.isSorting &&
      this.containerTarget.dataset.sortableGroup === "items"
    ) {
      this.sortable.option("disabled", true);
    }
  }

  onStart(evt) {
    this.dispatch("sortingStarted", {
      detail: { item: evt.item },
    });
    // Prevent animations during sorting (used by animate_on_scroll_controller.js)
    window.isSorting = true;
    // Prevent text selection during drag
    document.body.classList.add("select-none");

    // Wrap sortable-fallback in a div with theme variables for proper styling
    this.duplicateThemeVariables();

    if (evt.item.dataset.sortableType === "section") {
      // Add class to body to trigger section content hiding
      document.body.classList.add("is-section-sorting");
      evt.item.classList.add("is-dragging");
      // Disable menu item sorting while section is being dragged
      document
        .querySelectorAll('[data-sortable-group="items"]')
        .forEach((el) => {
          const sortableInstance = Sortable.get(el);
          if (sortableInstance) sortableInstance.option("disabled", true);
        });

      this.scrollToElement(evt.item);
    } else if (evt.item.dataset.sortableType === "feature") {
      // Add class to body to trigger section content hiding during feature sorting
      document.body.classList.add("is-feature-sorting");
      this.scrollToElement(evt.item);
    } else if (evt.item.dataset.sortableType === "image") {
      // Add class to body to trigger section content hiding during image sorting
      document.body.classList.add("is-image-sorting");
      this.scrollToElement(evt.item);
    } else if (evt.item.dataset.sortableType === "venue") {
      // Add class to body to trigger venue content hiding during venue sorting
      document.body.classList.add("is-venue-sorting");
      this.scrollToElement(evt.item);
    }
  }

  onEnd(evt) {
    this.dispatch("sortingFinished", {
      detail: { item: evt.item },
    });
    // Re-enable animations
    window.isSorting = false;
    // Re-enable text selection
    document.body.classList.remove("select-none");

    // Remove the wrapper div containing theme variables
    this.removeDuplicatedThemeVariables();

    if (evt.item.dataset.sortableType === "section") {
      // Remove the section sorting class from body
      document.body.classList.remove("is-section-sorting");
      evt.item.classList.remove("is-dragging");
      // Re-enable menu item sorting
      document
        .querySelectorAll('[data-sortable-group="items"]')
        .forEach((el) => {
          const sortableInstance = Sortable.get(el);
          if (sortableInstance) sortableInstance.option("disabled", false);
        });

      this.scrollToElement(evt.item);
    } else if (evt.item.dataset.sortableType === "feature") {
      // Remove the feature sorting class from body
      document.body.classList.remove("is-feature-sorting");
      this.scrollToElement(evt.item);
    } else if (evt.item.dataset.sortableType === "image") {
      // Remove the image sorting class from body
      document.body.classList.remove("is-image-sorting");
      this.scrollToElement(evt.item);
    } else if (evt.item.dataset.sortableType === "venue") {
      // Remove the venue sorting class from body
      document.body.classList.remove("is-venue-sorting");
      this.scrollToElement(evt.item);
    }

    // Only auto-submit if autoSubmit is true
    if (this.autoSubmitValue) {
      this.formTarget.requestSubmit();
    }
  }

  scrollToElement(element) {
    // Wait for the next frame to ensure all DOM updates are complete
    requestAnimationFrame(() => {
      // Find the scrollable element
      const scrollableElement = this.scrollableElement;
      if (!scrollableElement) return;

      // Calculate the element's position relative to the scrollable element
      const elementRect = element.getBoundingClientRect();
      const mainRect = scrollableElement.getBoundingClientRect();
      const relativeTop = elementRect.top - mainRect.top;

      // Scroll the scrollable element
      scrollableElement.scrollTo({
        top: scrollableElement.scrollTop + relativeTop - 150,
        // behavior: "smooth",
      });
    });
  }

  disconnect() {
    // sortable disconnects when sorting, because of the nature of sorting,
    // where we hide the actual content and show a preview, so we don't want
    // to clean up the sortable instance when sorting, otherwise it'll simply
    // stop working, since we disconnect it.
    if (window.isSorting) {
      return;
    }

    // Clean up the Sortable instance when the controller is disconnected
    if (this.sortable) {
      this.sortable.destroy();
      this.sortable = null;
    }

    // Clean up any global state that might have been left
    window.isSorting = false;
    document.body.classList.remove("select-none");
    document.body.classList.remove("is-section-sorting");
    document.body.classList.remove("is-feature-sorting");
    document.body.classList.remove("is-image-sorting");
    document.body.classList.remove("is-venue-sorting");
  }

  get scrollableElement() {
    if (this.hasRootElementSelectorValue) {
      return document.querySelector(this.rootElementSelectorValue);
    } else {
      return document.querySelector("main");
    }
  }

  duplicateThemeVariables() {
    // Wait for next frame to ensure .sortable-fallback is in DOM
    requestAnimationFrame(() => {
      const fallbackElement = document.querySelector(".sortable-fallback");
      if (!fallbackElement) return;

      // Find the original theme variables element
      const themeVariables = document.getElementById("theme-variables");
      if (!themeVariables) return;

      // Create wrapper div
      const wrapperDiv = document.createElement("div");
      wrapperDiv.id = "sortable-theme-wrapper";
      wrapperDiv.setAttribute("data-theme", "customer");
      wrapperDiv.classList.add("@container");
      // wrapperDiv.style.contain = "layout";

      // Clone the theme variables
      const clonedThemeVariables = themeVariables.cloneNode(true);
      clonedThemeVariables.id = "theme-variables-duplicate";

      // Insert wrapper after fallback element
      fallbackElement.insertAdjacentElement("afterend", wrapperDiv);

      // Move fallback element inside wrapper
      wrapperDiv.appendChild(fallbackElement);

      // Add cloned theme variables to wrapper
      wrapperDiv.appendChild(clonedThemeVariables);
    });
  }

  removeDuplicatedThemeVariables() {
    const wrapper = document.getElementById("sortable-theme-wrapper");
    if (wrapper) {
      // Move fallback element back out before wrapper (if it still exists)
      const fallbackElement = wrapper.querySelector(".sortable-fallback");
      if (fallbackElement) {
        wrapper.insertAdjacentElement("beforebegin", fallbackElement);
      }
      // Remove the wrapper
      wrapper.remove();
    }
  }
}
